import React, { useMemo, useState } from "react";
import { v4 as uuidV4 } from "uuid";
import { AlertCircle, CheckCircle2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import {
  PurchaseReleaseBreakdownItem,
  ListCfrTemplateItem,
  useListCfrTemplates,
} from "@/lib/api/cfr";
import { useBulkSendBatchSubmitter, type BulkSendInstancePayload } from "./useBulkSendBatchSubmitter";
import { BulkSendBatchProgressView } from "./BulkSendBatchProgressView";

interface BulkPurchaseReleaseBreakdownSendDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedItems: PurchaseReleaseBreakdownItem[];
}

export function BulkPurchaseReleaseBreakdownSendDialog({
  open,
  onOpenChange,
  selectedItems,
}: BulkPurchaseReleaseBreakdownSendDialogProps) {
  const { toast } = useToast();
  const templatesResult = useListCfrTemplates();
  const templateData: ListCfrTemplateItem[] = templatesResult.data?.data?.data || [];

  const { phase, isSubmitting, submit, close } = useBulkSendBatchSubmitter({
    onBeforeRedirect: () => {
      setIsTestSend(false);
      onOpenChange(false);
    },
  });

  const [isTestSend, setIsTestSend] = useState(false);

  const templateLookup = useMemo(() => {
    const map = new Map<string, string>();
    for (const template of templateData) {
      if (template.id && template.DocumentTemplate?.id) {
        map.set(template.id, template.DocumentTemplate.id);
      }
    }
    return map;
  }, [templateData]);

  const validationSummary = useMemo(() => {
    const validItems: Array<{ item: PurchaseReleaseBreakdownItem; templateId: string }> = [];
    const missingCustomer: PurchaseReleaseBreakdownItem[] = [];
    const missingTemplate: PurchaseReleaseBreakdownItem[] = [];

    for (const item of selectedItems) {
      const hasCustomer = !!item.customer_id;
      const templateId = item.document_template_id || templateLookup.get(item.id) || null;
      const hasTemplate = !!templateId;

      if (hasCustomer && hasTemplate) {
        validItems.push({ item, templateId: templateId! });
        continue;
      }

      if (!hasCustomer) {
        missingCustomer.push(item);
      }
      if (!hasTemplate) {
        missingTemplate.push(item);
      }
    }

    return {
      validItems,
      missingCustomer,
      missingTemplate,
    };
  }, [selectedItems, templateLookup]);

  const commoditySummary = useMemo(() => {
    const summaryMap = new Map<string, number>();
    for (const item of selectedItems) {
      const key = `${item.commodity_type || "unknown"}|${item.crop_year ?? "n/a"}`;
      summaryMap.set(key, (summaryMap.get(key) || 0) + 1);
    }
    return Array.from(summaryMap.entries()).map(([key, count]) => {
      const [commodity, cropYear] = key.split("|");
      return { commodity, cropYear, count };
    });
  }, [selectedItems]);

  const handleClose = () => {
    if (isSubmitting) { return; }
    close();
    setIsTestSend(false);
    onOpenChange(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;

    if (validationSummary.validItems.length === 0) {
      toast({
        title: "Cannot Send Contracts",
        description: "No selected items are eligible for sending.",
        variant: "destructive",
      });
      return;
    }

    const payloads: BulkSendInstancePayload[] = validationSummary.validItems.map(({ item, templateId }) => ({
      cfr_document_id: item.id,
      group_id: uuidV4(),
      template_id: templateId,
      customer_id: item.customer_id!,
      is_test_send: isTestSend,
      is_draft: false,
    }));

    await submit(payloads);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { handleClose(); } else { onOpenChange(o); } }}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Bulk Send Purchase Release Contracts</DialogTitle>
        </DialogHeader>

        {phase.kind !== "idle" ? (
          <BulkSendBatchProgressView phase={phase} onClose={handleClose} />
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Selected Contracts</Label>
                <div className="text-sm text-muted-foreground">
                  {selectedItems.length} item(s) selected
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="is_test_send">Is Test Send</Label>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_test_send"
                    checked={isTestSend}
                    onCheckedChange={(checked) => setIsTestSend(!!checked)}
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label>Validation Summary</Label>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="font-medium text-green-600">
                      {validationSummary.validItems.length} ready to send
                    </span>
                  </div>
                  {validationSummary.missingCustomer.length > 0 && (
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
                      <span className="text-yellow-700">
                        {validationSummary.missingCustomer.length} missing customer assignment
                      </span>
                    </div>
                  )}
                  {validationSummary.missingTemplate.length > 0 && (
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
                      <span className="text-red-700">
                        {validationSummary.missingTemplate.length} missing template metadata
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {commoditySummary.length > 0 && (
                <div className="grid gap-2">
                  <Label>Commodity &amp; Crop Year Mix</Label>
                  <div className="grid gap-1 text-sm text-muted-foreground">
                    {commoditySummary.map(({ commodity, cropYear, count }) => (
                      <div key={`${commodity}-${cropYear}`} className="flex justify-between">
                        <span>{`${commodity.toUpperCase()} > ${cropYear}`}</span>
                        <span>{count} item(s)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={validationSummary.validItems.length === 0}
              >
                {validationSummary.validItems.length === 0
                  ? "No Sendable Contracts"
                  : `Send ${validationSummary.validItems.length} Contract(s)`}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
